import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowPathIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const DEFAULT_MINUTE_PRICE_USD = 0.3;
const CREDIT_REFERENCE_MINUTE_PRICE_USD = 0.15;
const DEFAULT_COMBOS = [50, 100, 300, 500, 1000];

const fmtBrl = (n) =>
  typeof n === 'number' && Number.isFinite(n)
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n)
    : '—';

const fmtUsd = (n) =>
  typeof n === 'number' && Number.isFinite(n)
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
    : '—';

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(d);
};

export default function Cotacao() {
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'https://ia.discador.net').replace(/\/+$/, '');

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isAdminOrSuperAdmin = userData?.role === 'admin' || userData?.role === 'super_admin';
  const isSuperAdmin = userData?.role === 'super_admin';
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('BRL');
  const [loading, setLoading] = useState(false);
  const [loadingPixFor, setLoadingPixFor] = useState(null);
  const [result, setResult] = useState(null);
  const [comboPayload, setComboPayload] = useState(null);
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [clienteFinalNome, setClienteFinalNome] = useState('');
  const [clienteFinalEmail, setClienteFinalEmail] = useState('');
  const [pixCheckout, setPixCheckout] = useState(null);
  const [recargas, setRecargas] = useState([]);
  const [loadingPixQr, setLoadingPixQr] = useState(false);
  const [ultimoStatusRecargas, setUltimoStatusRecargas] = useState({});
  const [modalPagamento, setModalPagamento] = useState(null);

  const getAuthConfig = useMemo(
    () => () => {
      const token = localStorage.getItem('token') || localStorage.getItem('tokenClient') || '';
      return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
    },
    []
  );

  const cotacao = comboPayload?.cotacao;
  const getCreditReferenceUsd = useCallback(
    (row) => {
      const minutos = Number(row?.minutos || 0);
      const taxaCambioCartao = Number(row?.taxaCambioCartao ?? comboPayload?.taxaCambioCartao ?? 0.06);
      if (!Number.isFinite(minutos) || minutos <= 0) return null;
      const totalUsdCredito = minutos * CREDIT_REFERENCE_MINUTE_PRICE_USD;
      return Number((totalUsdCredito * (1 + taxaCambioCartao)).toFixed(2));
    },
    [comboPayload]
  );

  const getCreditReferenceBrl = useCallback(
    (row) => {
      const minutos = Number(row?.minutos || 0);
      const bid = Number(row?.taxaUsdParaBrl ?? comboPayload?.cotacao?.bid ?? 0);
      const taxaCambioCartao = Number(row?.taxaCambioCartao ?? comboPayload?.taxaCambioCartao ?? 0.06);
      if (!Number.isFinite(minutos) || minutos <= 0 || !Number.isFinite(bid) || bid <= 0) return null;
      const totalUsdCredito = minutos * CREDIT_REFERENCE_MINUTE_PRICE_USD;
      return Number((totalUsdCredito * bid * (1 + taxaCambioCartao)).toFixed(2));
    },
    [comboPayload]
  );

  const fetchRecargas = useCallback(async () => {
    try {
      const response = await axios.get(`${baseUrl}/api/asaas/recargas`, getAuthConfig());
      const lista = Array.isArray(response.data) ? response.data : [];

      // Detecta mudança de status para CREDITED para exibir modal em tempo real
      const novosStatus = {};
      lista.forEach((r) => {
        novosStatus[r._id] = r.status;
      });

      // Verifica se alguma recarga mudou de não-CREDITED para CREDITED
      const recargaCREDITED = lista.find((r) => {
        const antes = ultimoStatusRecargas[r._id];
        return r.status === 'CREDITED' && antes && antes !== 'CREDITED';
      });

      if (recargaCREDITED && !modalPagamento) {
        setModalPagamento({
          minutos: recargaCREDITED.minutos,
          valorBrl: recargaCREDITED.valorBrl,
          createdAt: recargaCREDITED.createdAt,
        });
      }

      // Sincroniza o card PIX com o status real da recarga e remove QR quando já estiver pago.
      if (pixCheckout?.payment?.id) {
        const recargaAtual = lista.find((r) => r.asaasPaymentId === pixCheckout.payment.id);
        if (recargaAtual) {
          if (recargaAtual.status === 'CREDITED') {
            setPixCheckout(null);
          } else {
            setPixCheckout((prev) => ({
              ...(prev || {}),
              payment: {
                ...(prev?.payment || {}),
                status: recargaAtual.status,
              },
            }));
          }
        }
      }

      setUltimoStatusRecargas(novosStatus);
      setRecargas(lista);
    } catch (_error) {
      // Não bloquear a tela caso usuário ainda não tenha integração ativa.
    }
  }, [baseUrl, getAuthConfig, ultimoStatusRecargas, modalPagamento, pixCheckout]);

  const fetchCombos = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/cotacao/combos-minutos`, getAuthConfig());
      setComboPayload(response.data);
    } catch (error) {
      const status = error.response?.status;
      if (status === 404) {
        // Fallback: backend antigo sem endpoint /combos-minutos.
        try {
          const quoteRes = await axios.get(`${baseUrl}/api/cotacao/usd-brl`, getAuthConfig());
          const bid = Number(quoteRes.data?.bid || 0);
          const combos = DEFAULT_COMBOS.map((minutos) => {
            const totalUsd = Number((minutos * DEFAULT_MINUTE_PRICE_USD).toFixed(2));
            return {
              minutos,
              totalUsd,
              totalBrl: Number((totalUsd * bid).toFixed(2)),
            };
          });
          setComboPayload({
            precoMinutoUsd: DEFAULT_MINUTE_PRICE_USD,
            minutosDisponiveis: DEFAULT_COMBOS,
            cotacao: quoteRes.data,
            combos,
            notaConversao:
              'Endpoint de combos não encontrado no backend. Exibindo cálculo local com cotação atual.',
          });
          toast.success('Combos carregados em modo compatível.');
        } catch (fallbackError) {
          toast.error(
            fallbackError.response?.data?.message || 'Erro ao carregar cotação e combos.'
          );
        }
      } else if (status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else {
        toast.error(error.response?.data?.message || 'Erro ao carregar cotação e combos.');
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl, getAuthConfig]);

  useEffect(() => {
    fetchCombos();
  }, [fetchCombos]);

  useEffect(() => {
    fetchRecargas();
  }, [fetchRecargas]);

  // Atualização periódica das recargas para refletir mudança de status em tempo quase real
  useEffect(() => {
    const id = setInterval(() => {
      fetchRecargas();
    }, 15000); // a cada 15s
    return () => clearInterval(id);
  }, [fetchRecargas]);

  const handleSwap = () => {
    setFrom(to);
    setTo(from);
    setResult(null);
  };

  const handleConvert = async () => {
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      toast.error('Informe um valor válido maior que zero.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${baseUrl}/api/cotacao/converter`,
        { amount: parsed, from, to },
        getAuthConfig()
      );
      setResult(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      } else {
        toast.error(error.response?.data?.message || 'Erro ao converter moeda.');
      }
    } finally {
      setLoading(false);
    }
  };

  const extractPixPayload = (pix) => {
    if (!pix) return '';
    return (
      pix.payload ||
      pix.copyPaste ||
      pix.emv ||
      pix.qrCode ||
      pix.pixCopiaECola ||
      ''
    );
  };

  const extractPixImage = (pix) => {
    if (!pix) return '';
    const raw =
      pix.encodedImage ||
      pix.qrcodeImage ||
      pix.qrCodeImage ||
      pix.qrCodeBase64 ||
      '';
    if (!raw) return '';
    if (raw.startsWith('data:image')) return raw;
    return `data:image/png;base64,${raw}`;
  };

  const handleCopyPixCode = async () => {
    const payload = extractPixPayload(pixCheckout?.pixQrCode);
    if (!payload) {
      toast.error('Código PIX copia e cola não disponível.');
      return;
    }
    try {
      await navigator.clipboard.writeText(payload);
      toast.success('Código PIX copiado!');
    } catch (_error) {
      toast.error('Não foi possível copiar automaticamente.');
    }
  };

  const handleGerarPix = async (combo) => {
    if (!isAdminOrSuperAdmin) {
      toast.error('Acesso permitido apenas para admin ou super_admin.');
      return;
    }
    const cpf = String(cpfCnpj || '').replace(/\D/g, '');
    if (cpf.length < 11) {
      toast.error('Informe um CPF/CNPJ válido antes de gerar PIX.');
      return;
    }
    if (!String(clienteFinalNome || '').trim()) {
      toast.error('Informe o nome do cliente final.');
      return;
    }
    if (!String(clienteFinalEmail || '').includes('@')) {
      toast.error('Informe um e-mail válido do cliente final.');
      return;
    }

    setLoadingPixFor(combo.minutos);
    try {
      const response = await axios.post(
        `${baseUrl}/api/asaas/checkout`,
        {
          minutos: combo.minutos,
          billingType: 'PIX',
          cpfCnpj: cpf,
          clienteFinalNome: String(clienteFinalNome || '').trim(),
          clienteFinalEmail: String(clienteFinalEmail || '').trim(),
        },
        getAuthConfig()
      );
      let checkoutData = response.data;

      // Algumas contas Asaas não retornam QR no payload inicial; tenta buscar logo em seguida.
      if (!checkoutData?.pixQrCode && checkoutData?.payment?.id) {
        try {
          setLoadingPixQr(true);
          const pixRes = await axios.get(
            `${baseUrl}/api/asaas/payments/${checkoutData.payment.id}/pix-qrcode`,
            getAuthConfig()
          );
          checkoutData = { ...checkoutData, pixQrCode: pixRes.data?.pixQrCode || null };
        } catch (_pixError) {
          // Mantém dados da cobrança mesmo sem QR.
        } finally {
          setLoadingPixQr(false);
        }
      }

      setPixCheckout(checkoutData);
      toast.success('Cobrança PIX gerada com sucesso.');
      fetchRecargas();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao gerar cobrança PIX.');
    } finally {
      setLoadingPixFor(null);
    }
  };

  const handleBuscarPixQr = async () => {
    const paymentId = pixCheckout?.payment?.id;
    if (!paymentId) {
      toast.error('Nenhuma cobrança disponível para buscar QR Code.');
      return;
    }
    setLoadingPixQr(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/asaas/payments/${paymentId}/pix-qrcode`,
        getAuthConfig()
      );
      setPixCheckout((prev) => ({
        ...(prev || {}),
        pixQrCode: response.data?.pixQrCode || null,
      }));
      toast.success('QR Code PIX atualizado.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Não foi possível buscar QR Code agora.');
    } finally {
      setLoadingPixQr(false);
    }
  };

  if (!isAdminOrSuperAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Acesso restrito</CardTitle>
          <CardDescription>
            A tela de câmbio está disponível apenas para admins e super_admins do white label.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {modalPagamento && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-card p-6 shadow-xl">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/10">
                <span className="text-2xl text-emerald-500">✓</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-emerald-500">Pagamento recebido!</h2>
                <p className="text-xs text-muted-foreground">
                  Sua recarga foi confirmada. Em até 1 hora os créditos irão aparecer na sua conta.
                </p>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              <p>
                <span className="text-muted-foreground">Pacote:</span>{' '}
                <span className="font-medium">{modalPagamento.minutos} minutos</span>
              </p>
              <p>
                <span className="text-muted-foreground">Valor pago:</span>{' '}
                <span className="font-mono">{fmtBrl(modalPagamento.valorBrl)}</span>
              </p>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                onClick={() => setModalPagamento(null)}
                variant="outline"
                className="border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
              >
                Entendi
              </Button>
            </div>
          </div>
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5" />
            Pacotes de minutos e câmbio USD/BRL
          </CardTitle>
          <CardDescription>
            Cada minuto equivale a US$ 0,30. Os valores em reais são uma estimativa com base na cotação
            atual (par USD-BRL).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Preço por minuto:{' '}
              <span className="font-medium text-foreground">
                {comboPayload?.precoMinutoUsd != null
                  ? fmtUsd(comboPayload.precoMinutoUsd)
                  : fmtUsd(DEFAULT_MINUTE_PRICE_USD)}{' '}
                USD
              </span>
            </p>
            <Button variant="outline" size="sm" onClick={fetchCombos} disabled={loading} className="gap-2">
              <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Atualizar cotação
            </Button>
          </div>

          <div className="grid gap-2 md:max-w-sm">
            <label className="text-sm text-muted-foreground">CPF/CNPJ (para gerar cobrança PIX no Asaas)</label>
            <Input
              value={cpfCnpj}
              onChange={(e) => setCpfCnpj(e.target.value)}
              placeholder="Somente números, ex: 12345678909"
            />
          </div>
          <div className="grid gap-2 md:max-w-sm">
            <label className="text-sm text-muted-foreground">Nome do cliente final</label>
            <Input
              value={clienteFinalNome}
              onChange={(e) => setClienteFinalNome(e.target.value)}
              placeholder="Ex.: João da Silva"
            />
          </div>
          <div className="grid gap-2 md:max-w-sm">
            <label className="text-sm text-muted-foreground">E-mail do cliente final</label>
            <Input
              value={clienteFinalEmail}
              onChange={(e) => setClienteFinalEmail(e.target.value)}
              placeholder="cliente@empresa.com"
            />
          </div>

          {comboPayload?.notaConversao && (
            <p className="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              {comboPayload.notaConversao}
            </p>
          )}
          <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-600">
            Aviso: será aplicado acréscimo de 6% sobre o valor em reais referente ao câmbio do cartão.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cotação atual — Dólar americano (USD) e Real brasileiro (BRL)</CardTitle>
          <CardDescription>
            Par {cotacao?.pair || 'USD-BRL'}: quanto vale 1 dólar em reais no mercado (referência AwesomeAPI).
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm md:grid-cols-2">
          <div className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-4">
            <p className="font-medium text-foreground">USD (dólar)</p>
            <p className="text-muted-foreground">
              Moeda base da cobrança dos pacotes: cada minuto = US$ 0,30.
            </p>
          </div>
          <div className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-4">
            <p className="font-medium text-foreground">BRL (real)</p>
            <p className="text-muted-foreground">
              O que o cliente paga em reais depende da cotação do dia abaixo.
            </p>
          </div>
          <div className="md:col-span-2 space-y-2 border-t border-border/60 pt-4">
            <p>
              <span className="text-muted-foreground">Compra (bid):</span>{' '}
              <span className="font-mono font-medium">
                1 USD = {cotacao?.bid != null ? fmtBrl(cotacao.bid) : '—'}
              </span>
            </p>
            <p>
              <span className="text-muted-foreground">Venda (ask):</span>{' '}
              <span className="font-mono font-medium">
                1 USD = {cotacao?.ask != null ? fmtBrl(cotacao.ask) : '—'}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              Variação do dia: {cotacao?.pctChange != null ? `${cotacao.pctChange}%` : '—'} · Atualizado:{' '}
              {cotacao?.create_date ?? '—'}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {isSuperAdmin
              ? 'Combos de minutos — valor em USD e estimativa em BRL'
              : 'Combos de minutos — crédito estimado em USD'}
          </CardTitle>
          <CardDescription>
            {isSuperAdmin
              ? 'Total em dólares = minutos × US$ 0,30. Total em reais ≈ total USD × taxa de compra (bid).'
              : 'Crédito estimado em dólar = minutos × US$ 0,30 com acréscimo de 6%.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full min-w-[480px] text-left text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Pacote</th>
                {isSuperAdmin && <th className="py-2 pr-4 font-medium">Total (USD)</th>}
                <th className="py-2 font-medium">
                  {isSuperAdmin ? 'Pagar em reais (estimativa)' : 'Crédito incluído em dólar (estimativa)'}
                </th>
                {!isSuperAdmin && <th className="py-2 font-medium">Valor a pagar em reais (estimativa)</th>}
                <th className="py-2 pl-4 font-medium">Ação</th>
              </tr>
            </thead>
            <tbody>
              {(comboPayload?.combos || []).map((row) => (
                <tr key={row.minutos} className="border-b border-border/60 last:border-0">
                  <td className="py-3 pr-4 font-medium">{row.minutos} minutos</td>
                  {isSuperAdmin && <td className="py-3 pr-4 font-mono">{fmtUsd(row.totalUsd)}</td>}
                  <td className="py-3 font-mono text-primary">
                    {isSuperAdmin
                      ? fmtBrl(row.totalBrl)
                      : fmtUsd(getCreditReferenceUsd(row))}
                  </td>
                  {!isSuperAdmin && <td className="py-3 font-mono text-primary">{fmtBrl(row.totalBrl)}</td>}
                  <td className="py-3 pl-4">
                    <Button
                      size="sm"
                      onClick={() => handleGerarPix(row)}
                      disabled={loadingPixFor !== null}
                    >
                      {loadingPixFor === row.minutos ? 'Gerando...' : 'Pagar com PIX'}
                    </Button>
                  </td>
                </tr>
              ))}
              {!comboPayload?.combos?.length && (
                <tr>
                  <td colSpan={isSuperAdmin ? 4 : 4} className="py-6 text-center text-muted-foreground">
                    {loading ? 'Carregando combos…' : 'Nenhum combo disponível.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pagamento PIX (Asaas)</CardTitle>
          <CardDescription>
            Gere o PIX em um dos combos e use os dados abaixo para pagar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {pixCheckout ? (
            <>
              <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-xs text-primary">
                Cobrança gerada com sucesso. Se o QR Code não aparecer de imediato, use o botão "Atualizar QR Code PIX".
              </div>
              <p>
                <span className="text-muted-foreground">Cobrança:</span>{' '}
                <span className="font-mono">{pixCheckout?.payment?.id || '-'}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Status:</span>{' '}
                <span className="font-medium">{pixCheckout?.payment?.status || '-'}</span>
              </p>
              <p>
                <span className="text-muted-foreground">Valor:</span>{' '}
                <span className="font-mono">{fmtBrl(Number(pixCheckout?.payment?.value || 0))}</span>
              </p>

              {extractPixImage(pixCheckout?.pixQrCode) ? (
                <img
                  src={extractPixImage(pixCheckout?.pixQrCode)}
                  alt="QR Code PIX"
                  className="h-56 w-56 rounded-md border border-border p-2"
                />
              ) : (
                <p className="text-muted-foreground">
                  QR Code de imagem não retornado pela API. Use o código copia e cola.
                </p>
              )}

              <div className="rounded-md border border-border/60 bg-muted/30 p-3">
                <p className="mb-2 text-xs text-muted-foreground">PIX copia e cola:</p>
                <p className="break-all font-mono text-xs">
                  {extractPixPayload(pixCheckout?.pixQrCode) || 'Não disponível'}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={handleCopyPixCode}>
                  Copiar código PIX
                </Button>
                <Button size="sm" variant="outline" onClick={handleBuscarPixQr} disabled={loadingPixQr}>
                  {loadingPixQr ? 'Buscando QR...' : 'Atualizar QR Code PIX'}
                </Button>
                {pixCheckout?.payment?.invoiceUrl && (
                  <Button size="sm" variant="outline" asChild>
                    <a href={pixCheckout.payment.invoiceUrl} target="_blank" rel="noreferrer">
                      Abrir fatura no Asaas
                    </a>
                  </Button>
                )}
              </div>
            </>
          ) : (
            <p className="text-muted-foreground">Nenhuma cobrança PIX gerada ainda.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Últimas recargas</CardTitle>
          <CardDescription>Histórico das últimas cobranças geradas no Asaas.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          {recargas.length === 0 ? (
            <p className="text-muted-foreground">Nenhuma recarga encontrada.</p>
          ) : (
            recargas.slice(0, 8).map((r) => (
              <div key={r._id} className="rounded-md border border-border/60 bg-muted/20 px-3 py-2">
                <p className="font-medium">
                  {r.minutos} minutos · {fmtBrl(r.valorBrl)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Cliente final: {r.clienteFinalNome || '-'} · {r.clienteFinalEmail || '-'}
                </p>
                <p className="text-xs text-muted-foreground">
                  Status:{' '}
                  <span
                    className={
                      r.status === 'CREDITED'
                        ? 'text-green-500 font-semibold'
                        : r.status === 'PENDING'
                        ? 'text-yellow-500'
                        : ''
                    }
                  >
                    {r.status === 'CREDITED' ? 'Pago' : r.status || '—'}
                  </span>
                  {' · '}Asaas: {r.asaasPaymentId}
                </p>
                <p className="text-xs text-muted-foreground">
                  {r.status === 'CREDITED' ? 'Pago em:' : 'Cobrança criada em:'}{' '}
                  {fmtDateTime(r.status === 'CREDITED' ? r.creditadoEm : r.createdAt)}
                </p>
                {r.status === 'CREDITED' && (
                  <p className="mt-1 text-xs text-green-500">
                    Pagamento recebido com sucesso. Em até 1 hora os créditos vão cair na sua conta.
                  </p>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Conversor livre (USD ↔ BRL)</CardTitle>
          <CardDescription>Use para simular qualquer valor além dos combos.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm text-muted-foreground">Valor</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ex.: 100"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm text-muted-foreground">De</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              >
                <option value="USD">USD</option>
                <option value="BRL">BRL</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm text-muted-foreground">Para</label>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              >
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={handleConvert} disabled={loading}>
              {loading ? 'Convertendo...' : 'Converter'}
            </Button>
            <Button variant="outline" onClick={handleSwap} disabled={loading}>
              Inverter moedas
            </Button>
          </div>

          {result && (
            <div className="rounded-md border border-border/60 bg-muted/30 p-4 text-sm space-y-1">
              <p>
                Valor original:{' '}
                <span className="font-mono">
                  {result.from === 'BRL' ? fmtBrl(result.originalAmount) : fmtUsd(result.originalAmount)}
                </span>
              </p>
              <p>
                Valor convertido:{' '}
                <span className="font-mono">
                  {result.to === 'BRL' ? fmtBrl(result.convertedAmount) : fmtUsd(result.convertedAmount)}
                </span>
              </p>
              <p className="text-muted-foreground">Taxa utilizada: {result.rateUsed}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
